/**
 * AI-Powered Order Insights Service
 * Provides intelligent analysis of order patterns, predictions, and recommendations
 * based on user behavior and order history
 */

import aiService from './aiService';

class AIOrderInsights {
    constructor() {
        this.orderHistory = JSON.parse(localStorage.getItem('ai_order_history') || '[]');
        this.insightsCache = new Map();
        this.cacheTimeout = 60 * 60 * 1000; // 1 hour
    }

    /**
     * Analyze order patterns and provide insights
     */
    async analyzeOrderPatterns(orders = []) {
        try {
            const prompt = `Analyze these order patterns and provide insights:

Orders: ${JSON.stringify(orders.slice(0, 20))} // Limit to recent 20 orders

Please provide:
1. Order frequency patterns (daily, weekly, monthly)
2. Preferred cuisines and dishes
3. Average order value trends
4. Peak ordering times
5. Seasonal preferences
6. Dietary pattern changes
7. Vendor preferences
8. Order size patterns

Return as JSON with detailed analysis.`;

            const response = await aiService.chatWithAI(prompt, { type: 'order_patterns' });
            
            if (response.success) {
                return this.parseAIResponse(response.data);
            }
        } catch (error) {
            console.error('Order patterns analysis error:', error);
        }

        return this.getFallbackOrderAnalysis(orders);
    }

    /**
     * Predict next order timing and preferences
     */
    async predictNextOrder(orders = [], userProfile = {}) {
        try {
            const prompt = `Predict the next order based on order history and user profile:

Recent Orders: ${JSON.stringify(orders.slice(0, 10))}
User Profile: ${JSON.stringify(userProfile)}
Current Time: ${new Date().toISOString()}

Please predict:
1. Next order timing (when they're likely to order next)
2. Preferred dishes for next order
3. Recommended vendors
4. Optimal order time suggestions
5. Price range prediction
6. Dietary preferences for next order

Return as JSON with predictions and confidence levels.`;

            const response = await aiService.chatWithAI(prompt, { type: 'order_prediction' });
            
            if (response.success) {
                return this.parseAIResponse(response.data);
            }
        } catch (error) {
            console.error('Order prediction error:', error);
        }

        return this.getFallbackOrderPrediction(orders);
    }

    /**
     * Analyze order value trends and provide recommendations
     */
    async analyzeOrderValueTrends(orders = []) {
        try {
            const prompt = `Analyze order value trends and provide recommendations:

Orders: ${JSON.stringify(orders.slice(0, 30))}

Please analyze:
1. Order value trends over time
2. Factors affecting order values
3. Seasonal spending patterns
4. Budget optimization suggestions
5. Value-for-money recommendations
6. Spending habit insights
7. Cost-saving opportunities

Return as JSON with value analysis and recommendations.`;

            const response = await aiService.chatWithAI(prompt, { type: 'order_value_analysis' });
            
            if (response.success) {
                return this.parseAIResponse(response.data);
            }
        } catch (error) {
            console.error('Order value analysis error:', error);
        }

        return this.getFallbackValueAnalysis(orders);
    }

    /**
     * Get personalized order recommendations
     */
    async getOrderRecommendations(orders = [], context = {}) {
        try {
            const cacheKey = `order_recs_${JSON.stringify(orders.slice(0, 5))}_${JSON.stringify(context)}`;
            
            // Check cache first
            if (this.insightsCache.has(cacheKey)) {
                const cached = this.insightsCache.get(cacheKey);
                if (Date.now() - cached.timestamp < this.cacheTimeout) {
                    return cached.data;
                }
            }

            const prompt = `Provide personalized order recommendations:

Order History: ${JSON.stringify(orders.slice(0, 15))}
Context: ${JSON.stringify(context)}
Current Time: ${new Date().toISOString()}

Please recommend:
1. 5 dishes they might like based on history
2. 3 vendors they should try
3. New cuisines to explore
4. Seasonal recommendations
5. Budget-friendly options
6. Healthy alternatives
7. Quick meal suggestions

Return as JSON with detailed recommendations and reasons.`;

            const response = await aiService.chatWithAI(prompt, { type: 'order_recommendations' });
            
            if (response.success) {
                const recommendations = this.parseAIResponse(response.data);
                
                // Cache the result
                this.insightsCache.set(cacheKey, {
                    data: recommendations,
                    timestamp: Date.now()
                });
                
                return recommendations;
            }
        } catch (error) {
            console.error('Order recommendations error:', error);
        }

        return this.getFallbackOrderRecommendations(orders, context);
    }

    /**
     * Analyze delivery patterns and optimize timing
     */
    async analyzeDeliveryPatterns(orders = []) {
        try {
            const prompt = `Analyze delivery patterns and provide optimization insights:

Orders: ${JSON.stringify(orders.filter(o => o.delivery_info).slice(0, 20))}

Please analyze:
1. Preferred delivery times
2. Delivery duration patterns
3. Location-based delivery insights
4. Optimal ordering times for faster delivery
5. Delivery success rate factors
6. Peak delivery hours
7. Delivery optimization suggestions

Return as JSON with delivery analysis.`;

            const response = await aiService.chatWithAI(prompt, { type: 'delivery_analysis' });
            
            if (response.success) {
                return this.parseAIResponse(response.data);
            }
        } catch (error) {
            console.error('Delivery patterns analysis error:', error);
        }

        return this.getFallbackDeliveryAnalysis(orders);
    }

    /**
     * Get order health insights
     */
    async getOrderHealthInsights(orders = []) {
        try {
            const prompt = `Analyze order health and provide wellness insights:

Orders: ${JSON.stringify(orders.slice(0, 30))}

Please analyze:
1. Nutritional balance of orders
2. Dietary diversity
3. Healthy eating patterns
4. Areas for improvement
5. Wellness recommendations
6. Balanced meal suggestions
7. Health-conscious alternatives

Return as JSON with health insights and recommendations.`;

            const response = await aiService.chatWithAI(prompt, { type: 'order_health' });
            
            if (response.success) {
                return this.parseAIResponse(response.data);
            }
        } catch (error) {
            console.error('Order health analysis error:', error);
        }

        return this.getFallbackHealthAnalysis(orders);
    }

    /**
     * Analyze seasonal ordering patterns
     */
    async analyzeSeasonalPatterns(orders = []) {
        try {
            const prompt = `Analyze seasonal ordering patterns:

Orders: ${JSON.stringify(orders.slice(0, 50))}
Current Season: ${this.getCurrentSeason()}

Please analyze:
1. Seasonal cuisine preferences
2. Weather-based ordering patterns
3. Holiday ordering trends
4. Seasonal menu recommendations
5. Festive food preferences
6. Seasonal dietary changes
7. Weather-appropriate food suggestions

Return as JSON with seasonal analysis.`;

            const response = await aiService.chatWithAI(prompt, { type: 'seasonal_analysis' });
            
            if (response.success) {
                return this.parseAIResponse(response.data);
            }
        } catch (error) {
            console.error('Seasonal patterns analysis error:', error);
        }

        return this.getFallbackSeasonalAnalysis(orders);
    }

    /**
     * Get order optimization suggestions
     */
    async getOrderOptimizationSuggestions(orders = []) {
        try {
            const prompt = `Provide order optimization suggestions:

Orders: ${JSON.stringify(orders.slice(0, 20))}

Please suggest:
1. Cost optimization strategies
2. Time-saving ordering tips
3. Bulk ordering opportunities
4. Loyalty program maximization
5. Delivery optimization
6. Menu planning suggestions
7. Budget management tips

Return as JSON with optimization suggestions.`;

            const response = await aiService.chatWithAI(prompt, { type: 'order_optimization' });
            
            if (response.success) {
                return this.parseAIResponse(response.data);
            }
        } catch (error) {
            console.error('Order optimization error:', error);
        }

        return this.getFallbackOptimizationSuggestions(orders);
    }

    /**
     * Record order for learning
     */
    recordOrder(order) {
        const orderEntry = {
            ...order,
            timestamp: new Date().toISOString(),
            analyzed: false
        };

        this.orderHistory.unshift(orderEntry);
        
        // Keep only last 100 orders
        if (this.orderHistory.length > 100) {
            this.orderHistory = this.orderHistory.slice(0, 100);
        }

        localStorage.setItem('ai_order_history', JSON.stringify(this.orderHistory));
    }

    /**
     * Get comprehensive order insights
     */
    async getComprehensiveInsights(orders = []) {
        try {
            const [
                patterns,
                predictions,
                valueTrends,
                recommendations,
                deliveryPatterns,
                healthInsights,
                seasonalPatterns,
                optimization
            ] = await Promise.all([
                this.analyzeOrderPatterns(orders),
                this.predictNextOrder(orders),
                this.analyzeOrderValueTrends(orders),
                this.getOrderRecommendations(orders),
                this.analyzeDeliveryPatterns(orders),
                this.getOrderHealthInsights(orders),
                this.analyzeSeasonalPatterns(orders),
                this.getOrderOptimizationSuggestions(orders)
            ]);

            return {
                success: true,
                insights: {
                    patterns,
                    predictions,
                    valueTrends,
                    recommendations,
                    deliveryPatterns,
                    healthInsights,
                    seasonalPatterns,
                    optimization
                },
                generatedAt: new Date().toISOString()
            };
        } catch (error) {
            console.error('Comprehensive insights error:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Parse AI response that might be wrapped in markdown code blocks
     */
    parseAIResponse(response) {
        try {
            // Remove markdown code block formatting if present
            let cleanResponse = response.trim();
            
            // Remove ```json and ``` if present
            if (cleanResponse.startsWith('```json')) {
                cleanResponse = cleanResponse.replace(/^```json\s*/, '').replace(/\s*```$/, '');
            } else if (cleanResponse.startsWith('```')) {
                cleanResponse = cleanResponse.replace(/^```\s*/, '').replace(/\s*```$/, '');
            }
            
            // Try to parse as JSON
            return JSON.parse(cleanResponse);
        } catch (error) {
            console.error('Failed to parse AI response:', error);
            console.log('Raw response:', response);
            
            // If parsing fails, try to extract JSON-like content
            try {
                // Look for JSON-like patterns in the response
                const jsonMatch = response.match(/\{[\s\S]*\}/);
                if (jsonMatch) {
                    return JSON.parse(jsonMatch[0]);
                }
                
                // Look for array-like patterns
                const arrayMatch = response.match(/\[[\s\S]*\]/);
                if (arrayMatch) {
                    return JSON.parse(arrayMatch[0]);
                }
            } catch (secondError) {
                console.error('Secondary parsing also failed:', secondError);
            }
            
            // Return null if all parsing attempts fail
            return null;
        }
    }

    /**
     * Get current season
     */
    getCurrentSeason() {
        const month = new Date().getMonth();
        if (month >= 2 && month <= 4) return 'Spring';
        if (month >= 5 && month <= 7) return 'Summer';
        if (month >= 8 && month <= 10) return 'Autumn';
        return 'Winter';
    }

    /**
     * Fallback methods when AI is not available
     */
    getFallbackOrderAnalysis(orders) {
        const totalOrders = orders.length;
        const totalValue = orders.reduce((sum, order) => sum + (order.total || 0), 0);
        const avgValue = totalOrders > 0 ? totalValue / totalOrders : 0;

        return {
            total_orders: totalOrders,
            total_value: totalValue,
            average_order_value: avgValue,
            most_ordered_cuisine: 'Indian',
            peak_ordering_time: 'Evening',
            order_frequency: 'Weekly'
        };
    }

    getFallbackOrderPrediction(orders) {
        return {
            next_order_timing: 'Within 3-5 days',
            confidence: 'Medium',
            recommended_dishes: ['Popular dishes from your history'],
            suggested_vendors: ['Your favorite vendors'],
            optimal_order_time: 'Evening'
        };
    }

    getFallbackValueAnalysis(orders) {
        const totalValue = orders.reduce((sum, order) => sum + (order.total || 0), 0);
        const avgValue = orders.length > 0 ? totalValue / orders.length : 0;

        return {
            total_spent: totalValue,
            average_order_value: avgValue,
            trend: 'Stable',
            recommendations: ['Consider bulk ordering for better value']
        };
    }

    getFallbackOrderRecommendations(orders, context) {
        return {
            dishes: ['Butter Chicken', 'Biryani', 'Paneer Tikka'],
            vendors: ['Local favorites'],
            new_cuisines: ['Try Thai or Chinese'],
            seasonal_recommendations: ['Hot soups for winter'],
            budget_options: ['Look for combo deals']
        };
    }

    getFallbackDeliveryAnalysis(orders) {
        return {
            average_delivery_time: '45 minutes',
            preferred_delivery_time: 'Evening',
            optimal_ordering_time: '30 minutes before desired delivery',
            delivery_success_rate: '95%'
        };
    }

    getFallbackHealthAnalysis(orders) {
        return {
            nutritional_balance: 'Good',
            dietary_diversity: 'Moderate',
            recommendations: ['Add more vegetables to your orders'],
            healthy_alternatives: ['Grilled options instead of fried']
        };
    }

    getFallbackSeasonalAnalysis(orders) {
        return {
            current_season_preferences: 'Comfort foods',
            seasonal_recommendations: ['Hot beverages and soups'],
            festive_trends: ['Traditional dishes during festivals']
        };
    }

    getFallbackOptimizationSuggestions(orders) {
        return {
            cost_savings: ['Order during happy hours'],
            time_saving: ['Use quick reorder feature'],
            bulk_ordering: ['Consider family packs'],
            loyalty_tips: ['Join loyalty programs for discounts']
        };
    }

    /**
     * Clear insights cache
     */
    clearCache() {
        this.insightsCache.clear();
    }

    /**
     * Get insights statistics
     */
    getInsightsStats() {
        return {
            total_orders_analyzed: this.orderHistory.length,
            cache_size: this.insightsCache.size,
            last_analysis: this.orderHistory[0]?.timestamp || null
        };
    }
}

// Create singleton instance
const aiOrderInsights = new AIOrderInsights();

export default aiOrderInsights;
